import React from 'react';
import { Spinner } from 'reactstrap';
import {
  FiChevronRight,
  FiChevronDown,
  FiChevronsLeft,
  FiChevronsRight,
  FiPlus,
  FiBookOpen,
  FiMusic,
  FiFileText,
  FiFolder,
  FiArchive,
} from 'react-icons/fi';

export type TreeSelection =
  | { type: 'course'; course: any }
  | { type: 'resources' }
  | { type: 'songs' }
  | { type: 'practice' }
  | null;

type CourseTreeProps = {
  courses: any[];
  isLoading: boolean;
  selection: TreeSelection;
  onSelect: (selection: TreeSelection) => void;
  onAddCourse: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  canAccessPractice?: boolean;
};

const courseId = (course: any) => course?.course_id ?? course?.id;

const isCourseSelected = (selection: TreeSelection, course: any) =>
  selection?.type === 'course' && courseId(selection.course) === courseId(course);

const CourseTree: React.FC<CourseTreeProps> = ({
  courses,
  isLoading,
  selection,
  onSelect,
  onAddCourse,
  collapsed,
  onToggleCollapse,
  canAccessPractice = false,
}) => {
  // CourseContent group is open by default. Selecting a course auto-opens it,
  // but the chevron can always collapse it afterwards.
  const [courseGroupOpen, setCourseGroupOpen] = React.useState(true);
  const selectedCourseId = selection?.type === 'course' ? courseId(selection.course) : null;
  React.useEffect(() => {
    if (selectedCourseId != null) {
      setCourseGroupOpen(true);
    }
  }, [selectedCourseId]);
  const groupOpen = courseGroupOpen;

  if (collapsed) {
    return (
      <div className="course-tree course-tree--collapsed">
        <button
          type="button"
          className="course-tree__rail-btn"
          title="Expand panel"
          onClick={onToggleCollapse}
        >
          <FiChevronsRight />
        </button>
        <button
          type="button"
          className="course-tree__rail-btn"
          title="Add Course"
          onClick={onAddCourse}
        >
          <FiPlus />
        </button>
        <button
          type="button"
          className={`course-tree__rail-btn ${selection?.type === 'course' ? 'is-active' : ''}`}
          title="Course Content"
          onClick={() => onToggleCollapse()}
        >
          <FiBookOpen />
        </button>
        <button
          type="button"
          className={`course-tree__rail-btn ${selection?.type === 'resources' ? 'is-active' : ''}`}
          title="Resources"
          onClick={() => onSelect({ type: 'resources' })}
        >
          <FiArchive />
        </button>
        <button
          type="button"
          className={`course-tree__rail-btn ${selection?.type === 'songs' ? 'is-active' : ''}`}
          title="Songs"
          onClick={() => onSelect({ type: 'songs' })}
        >
          <FiMusic />
        </button>
        {canAccessPractice && (
          <button
            type="button"
            className={`course-tree__rail-btn ${selection?.type === 'practice' ? 'is-active' : ''}`}
            title="Practice Material"
            onClick={() => onSelect({ type: 'practice' })}
          >
            <FiFileText />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="course-tree">
      <div className="course-tree__header">
        <span className="course-tree__title">Library</span>
        <button
          type="button"
          className="course-tree__icon-btn"
          title="Collapse panel"
          onClick={onToggleCollapse}
        >
          <FiChevronsLeft />
        </button>
      </div>

      <button type="button" className="course-tree__add-btn" onClick={onAddCourse}>
        <FiPlus />
        <span>Add Course</span>
      </button>

      <nav className="course-tree__nav">
        {/* CourseContent group */}
        <button
          type="button"
          className="course-tree__group"
          onClick={() => setCourseGroupOpen((prev) => !prev)}
        >
          <span className="course-tree__chevron">
            {groupOpen ? <FiChevronDown /> : <FiChevronRight />}
          </span>
          <FiBookOpen className="course-tree__node-icon" />
          <span className="course-tree__label">CourseContent</span>
        </button>

        {groupOpen && (
          <div className="course-tree__children">
            {isLoading ? (
              <div className="course-tree__loading">
                <Spinner size="sm" color="primary" /> <span>Loading…</span>
              </div>
            ) : courses.length === 0 ? (
              <div className="course-tree__empty">No courses yet</div>
            ) : (
              courses.map((course: any) => (
                <button
                  type="button"
                  key={courseId(course)}
                  className={`course-tree__leaf ${
                    isCourseSelected(selection, course) ? 'is-active' : ''
                  }`}
                  onClick={() => onSelect({ type: 'course', course })}
                  title={course.course_name}
                >
                  <FiFolder className="course-tree__node-icon" />
                  <span className="course-tree__label">{course.course_name}</span>
                </button>
              ))
            )}
          </div>
        )}

        {/* Resources */}
        <button
          type="button"
          className={`course-tree__top-leaf ${selection?.type === 'resources' ? 'is-active' : ''}`}
          onClick={() => onSelect({ type: 'resources' })}
        >
          <FiArchive className="course-tree__node-icon" />
          <span className="course-tree__label">Resources</span>
        </button>

        {/* Songs */}
        <button
          type="button"
          className={`course-tree__top-leaf ${selection?.type === 'songs' ? 'is-active' : ''}`}
          onClick={() => onSelect({ type: 'songs' })}
        >
          <FiMusic className="course-tree__node-icon" />
          <span className="course-tree__label">Songs</span>
        </button>

        {/* Practice Material */}
        {canAccessPractice && (
          <button
            type="button"
            className={`course-tree__top-leaf ${selection?.type === 'practice' ? 'is-active' : ''}`}
            onClick={() => onSelect({ type: 'practice' })}
          >
            <FiFileText className="course-tree__node-icon" />
            <span className="course-tree__label">Practice Material</span>
          </button>
        )}
      </nav>
    </div>
  );
};

export default CourseTree;
